import * as React from "react"
import { Link, usePage } from "@inertiajs/react"
import {
  BrochureButton,
  BrochureGateProvider,
  BrochureLink,
  useBrochureAvailable,
} from "@/components/public/BrochureGate"
import { FlashMessages } from "@/components/public/FlashMessages"
import { SelectionTray } from "@/components/public/SelectionTray"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/ui/theme-toggle"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

// Public shell for the marketing site, built to the Private Cellar mockup
// (research/design-direction/mockup-b-private-cellar.html). Styling lives in
// the "Public site" component classes in design-system.css.

type NavLink = { href: string; label: string }

const OCCASIONS: NavLink[] = [
  { href: "/occasions/corporate", label: "Corporate" },
  { href: "/occasions/weddings", label: "Weddings" },
  { href: "/occasions/hospitality", label: "Hospitality" },
  { href: "/occasions/private-events", label: "Private Events" },
  { href: "/occasions/sporting-events", label: "Sporting Events" },
]

const WINES_LINK: NavLink = { href: "/wines", label: "Our Wines" }

const NAV_AFTER_OCCASIONS: NavLink[] = [
  { href: "/how-it-works", label: "How It Works" },
  { href: "/gallery", label: "Gallery" },
  { href: "/studio", label: "Studio" },
  { href: "/about", label: "About" },
]

const EXPLORE_LINKS: NavLink[] = [
  { href: "/", label: "Home" },
  WINES_LINK,
  ...NAV_AFTER_OCCASIONS,
  { href: "/faqs", label: "FAQs" },
  { href: "/contact", label: "Contact" },
]

const LEGAL_LINKS: NavLink[] = [
  { href: "/privacy", label: "Privacy Policy" },
  { href: "/terms", label: "Terms & Conditions" },
]

const CONSULTATION_HREF = "/contact"

function isActive(url: string, href: string) {
  const path = url.split(/[?#]/)[0]
  if (href === "/") return path === "/"
  return path === href || path.startsWith(`${href}/`)
}

function current(url: string, href: string) {
  return isActive(url, href) ? ("page" as const) : undefined
}

// Duncan's admin area (M7): linked for admins only, from the drawer and
// the footer (the starter shell's MainNav has its own link).
const ADMIN_HREF = "/admin/dashboard"

function useAccount() {
  const { props } = usePage<PageProps>()
  const signedIn = Boolean(props.current_user)
  return {
    signedIn,
    isAdmin: Boolean(props.current_user?.admin),
    accountHref: signedIn ? "/account" : "/login",
  }
}

export function PublicShell({ children }: { children: React.ReactNode }) {
  return (
    // The brochure gate (M7): one dialog and one download per page, opened
    // by every "Download our brochure" link inside the shell.
    <BrochureGateProvider>
      <div className="flex min-h-screen flex-col bg-page">
        <PublicHeader />
        {/* The one-time flash, once for every public page (M7): docked under the header. */}
        <FlashMessages />
        <main className="min-w-0 flex-1">{children}</main>
        <PublicFooter />
        <SelectionTray />
      </div>
    </BrochureGateProvider>
  )
}

function Wordmark({ onNavigate }: { onNavigate?: () => void }) {
  return (
    <Link href="/" onClick={onNavigate} className="wordmark" aria-label="SOH GD Wines — home">
      SOH GD <em>Wines</em>
    </Link>
  )
}

function Chevron() {
  return (
    <svg width="9" height="6" viewBox="0 0 9 6" fill="none" aria-hidden="true">
      <path d="M1 1l3.5 3.5L8 1" stroke="currentColor" strokeWidth="1.2" />
    </svg>
  )
}

function AccountIcon() {
  return (
    <svg width="21" height="21" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="12" cy="8" r="3.6" stroke="currentColor" strokeWidth="1.3" />
      <path
        d="M4.6 20c.9-3.7 3.8-5.6 7.4-5.6S18.5 16.3 19.4 20"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  )
}

function PublicHeader() {
  const { url } = usePage()
  const { signedIn, accountHref } = useAccount()
  const [scrolled, setScrolled] = React.useState(false)
  const [drawerOpen, setDrawerOpen] = React.useState(false)
  const burgerRef = React.useRef<HTMLButtonElement>(null)

  // Header gains its blurred fill once the page scrolls past 24px.
  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24)
    onScroll()
    window.addEventListener("scroll", onScroll, { passive: true })
    return () => window.removeEventListener("scroll", onScroll)
  }, [])

  React.useEffect(() => {
    setDrawerOpen(false)
  }, [url])

  const closeDrawer = React.useCallback(() => {
    setDrawerOpen(false)
    burgerRef.current?.focus()
  }, [])

  return (
    <>
      <header className={cn("site-header", scrolled && "scrolled")}>
        <div className="shell site-header-bar">
          <Wordmark />

          <nav aria-label="Primary" className="ml-auto hidden items-center gap-[24px] xl:flex">
            <Link href={WINES_LINK.href} className="nav-link" aria-current={current(url, WINES_LINK.href)}>
              {WINES_LINK.label}
            </Link>
            <OccasionsDropdown url={url} />
            {NAV_AFTER_OCCASIONS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                className="nav-link"
                aria-current={current(url, link.href)}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-[18px] xl:ml-0">
            <Link
              href={accountHref}
              className="acct hidden xl:inline-flex"
              aria-label={signedIn ? "My account" : "Sign in"}
            >
              <AccountIcon />
            </Link>
            <Button asChild variant="gold" size="sm" className="hidden xl:inline-flex">
              <Link href={CONSULTATION_HREF}>Request a Consultation</Link>
            </Button>
            <button
              ref={burgerRef}
              type="button"
              className="burger xl:hidden"
              aria-label="Open menu"
              aria-expanded={drawerOpen}
              aria-controls="site-drawer"
              onClick={() => setDrawerOpen(true)}
            >
              <span />
            </button>
          </div>
        </div>
      </header>

      {/* Rendered outside <header> on purpose: the scrolled header's
          backdrop-filter creates a containing block that would trap this
          fixed overlay. */}
      <MobileDrawer open={drawerOpen} onClose={closeDrawer} url={url} />
    </>
  )
}

function OccasionsDropdown({ url }: { url: string }) {
  const [open, setOpen] = React.useState(false)
  const rootRef = React.useRef<HTMLDivElement>(null)
  const triggerRef = React.useRef<HTMLButtonElement>(null)
  const panelId = React.useId()

  React.useEffect(() => {
    setOpen(false)
  }, [url])

  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }
    document.addEventListener("pointerdown", onPointerDown)
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.removeEventListener("pointerdown", onPointerDown)
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open])

  const onTriggerKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key !== "ArrowDown") return
    event.preventDefault()
    setOpen(true)
    // Wait a frame so the panel is visible (visibility transitions) before focusing.
    requestAnimationFrame(() => {
      rootRef.current?.querySelector<HTMLAnchorElement>(".dd-item")?.focus()
    })
  }

  // Close when keyboard focus leaves the dropdown entirely.
  const onBlur = (event: React.FocusEvent<HTMLDivElement>) => {
    if (!rootRef.current?.contains(event.relatedTarget as Node | null)) setOpen(false)
  }

  return (
    <div ref={rootRef} className={cn("dd", open && "open")} onBlur={onBlur}>
      <button
        ref={triggerRef}
        type="button"
        className="nav-link dd-trigger"
        aria-expanded={open}
        aria-controls={panelId}
        aria-current={url.startsWith("/occasions") ? "page" : undefined}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={onTriggerKeyDown}
      >
        Occasions
        <Chevron />
      </button>
      <div id={panelId} className="dd-panel">
        {OCCASIONS.map((occasion) => (
          <Link
            key={occasion.href}
            href={occasion.href}
            className="dd-item"
            aria-current={current(url, occasion.href)}
          >
            {occasion.label}
          </Link>
        ))}
        <div className="dd-note">
          Not sure which fits? <Link href={CONSULTATION_HREF}>Request a consultation</Link>
        </div>
      </div>
    </div>
  )
}

function MobileDrawer({
  open,
  onClose,
  url,
}: {
  open: boolean
  onClose: () => void
  url: string
}) {
  const { signedIn, isAdmin, accountHref } = useAccount()
  const [occasionsOpen, setOccasionsOpen] = React.useState(url.startsWith("/occasions"))
  const closeRef = React.useRef<HTMLButtonElement>(null)

  React.useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    closeRef.current?.focus()
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose()
    }
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [open, onClose])

  return (
    <div
      id="site-drawer"
      className={cn("drawer xl:hidden", open && "open")}
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
      aria-hidden={!open}
    >
      <div className="drawer-top">
        <Wordmark onNavigate={onClose} />
        <button
          ref={closeRef}
          type="button"
          className="drawer-close"
          aria-label="Close menu"
          onClick={onClose}
        >
          &times;
        </button>
      </div>

      <nav aria-label="Mobile" className="drawer-nav">
        <Link href={WINES_LINK.href} className="drawer-link" aria-current={current(url, WINES_LINK.href)}>
          {WINES_LINK.label}
        </Link>
        <div>
          <button
            type="button"
            className="drawer-acc-trigger"
            aria-expanded={occasionsOpen}
            aria-controls="drawer-occasions"
            onClick={() => setOccasionsOpen((value) => !value)}
          >
            Occasions
            <Chevron />
          </button>
          {occasionsOpen && (
            <div id="drawer-occasions" className="drawer-acc-list">
              {OCCASIONS.map((occasion) => (
                <Link
                  key={occasion.href}
                  href={occasion.href}
                  aria-current={current(url, occasion.href)}
                >
                  {occasion.label}
                </Link>
              ))}
            </div>
          )}
        </div>
        {NAV_AFTER_OCCASIONS.map((link) => (
          <Link
            key={link.href}
            href={link.href}
            className="drawer-link"
            aria-current={current(url, link.href)}
          >
            {link.label}
          </Link>
        ))}
      </nav>

      <div className="drawer-foot">
        <Button asChild variant="gold" size="lg" className="w-full">
          <Link href={CONSULTATION_HREF}>Request a Consultation</Link>
        </Button>
        <Link href={accountHref} className="drawer-quiet">
          {signedIn ? "My Account" : "Sign in / My Account"}
        </Link>
        {isAdmin && (
          <Link href={ADMIN_HREF} className="drawer-quiet">
            Admin
          </Link>
        )}
      </div>
    </div>
  )
}

function PublicFooter() {
  const { signedIn, isAdmin, accountHref } = useAccount()
  const brochureAvailable = useBrochureAvailable()

  return (
    <footer className="site-footer">
      <div className="shell">
        <div className="foot-grid">
          <div className="foot-brand">
            <Wordmark />
            <p>Premium South African wine, bespoke-labelled for the occasions that deserve it.</p>
          </div>
          <FooterColumn
            heading="Explore"
            links={EXPLORE_LINKS}
            extra={brochureAvailable ? <BrochureLink /> : undefined}
          />
          <FooterColumn heading="Occasions" links={OCCASIONS} />
          <FooterColumn
            heading="Account & Legal"
            links={[
              { href: accountHref, label: signedIn ? "My Account" : "Sign in / My Account" },
              ...(isAdmin ? [{ href: ADMIN_HREF, label: "Admin" }] : []),
              ...LEGAL_LINKS,
            ]}
          />
        </div>

        <div className="foot-util">
          <span>&copy; SOH GD Wines. All rights reserved.</span>
          <ThemeToggle variant="segment" />
        </div>
      </div>
    </footer>
  )
}

function FooterColumn({
  heading,
  links,
  extra,
}: {
  heading: string
  links: NavLink[]
  /** A last item that is not a link (the brochure gate's button). */
  extra?: React.ReactNode
}) {
  return (
    <div className="foot-col">
      <h2 className="footer-heading">{heading}</h2>
      <ul>
        {links.map((link) => (
          <li key={link.href}>
            <Link href={link.href} className="footer-link">
              {link.label}
            </Link>
          </li>
        ))}
        {extra && <li>{extra}</li>}
      </ul>
    </div>
  )
}

/**
 * The consultation call-to-action band (mockup `.cta`). Pages render it as
 * their last section, directly above the footer — it is not part of the footer.
 * The `brochure` variant (M7) adds "Download our brochure" beside the main
 * action; it disappears on its own when no brochure is attached.
 */
export function ConsultationBand({
  eyebrow = "Let's begin",
  title = "Let us put your brand on the bottle",
  body = "Tell us about your occasion and we will guide you through the wines, the label and the delivery.",
  actionLabel = "Request a Consultation",
  actionHref = CONSULTATION_HREF,
  brochure = false,
}: {
  eyebrow?: string
  title?: React.ReactNode
  body?: React.ReactNode
  actionLabel?: string
  actionHref?: string
  brochure?: boolean
}) {
  return (
    <section className="cta" aria-labelledby="consultation-band-title">
      <div className="shell">
        <div className="eyebrow eyebrow-center">{eyebrow}</div>
        <h2 id="consultation-band-title">{title}</h2>
        <p>{body}</p>
        <div className="cta-actions">
          <Button asChild variant="gold" size="lg">
            <Link href={actionHref}>{actionLabel}</Link>
          </Button>
          {brochure && <BrochureButton />}
        </div>
      </div>
    </section>
  )
}

export type PublicHeroImage = {
  src: string
  alt: string
  width?: number
  height?: number
}

/**
 * Page hero (mockup `.hero`): copy on the left, optional media on the right
 * with a mask fade, two scrims and a gold glow. Without an image it renders
 * as a compact, copy-only band so pages still clear the fixed header.
 */
export function PublicHero({
  eyebrow,
  title,
  lede,
  actions,
  image,
  rule,
}: {
  eyebrow?: React.ReactNode
  title: React.ReactNode
  lede?: React.ReactNode
  actions?: React.ReactNode
  image?: PublicHeroImage
  rule?: React.ReactNode
}) {
  return (
    <section className={cn("hero", !image && "hero-compact")}>
      {image && (
        <div className="hero-media">
          <img
            src={image.src}
            alt={image.alt}
            width={image.width}
            height={image.height}
            fetchPriority="high"
          />
          <div className="hero-scrim hero-scrim-a" />
          <div className="hero-scrim hero-scrim-b" />
        </div>
      )}
      <div className="hero-glow" aria-hidden="true" />

      <div className="hero-inner">
        <div className="shell">
          <div className="hero-copy">
            {eyebrow && <div className="eyebrow">{eyebrow}</div>}
            <h1>{title}</h1>
            {lede && <p className="lede">{lede}</p>}
            {actions && <div className="hero-actions">{actions}</div>}
            {rule && <div className="hero-rule">{rule}</div>}
          </div>
        </div>
      </div>
    </section>
  )
}
